<div #cardsRoot class="relative flex h-full flex-col gap-4" [style.width.px]="phoneWidth()">
  <!-- Allows to add a card containing the uploading list on mobile -->
  <ng-content></ng-content>
  @if (loaded()) {
    @if (display() === CaseDisplay.List) {
      <!--
       The whole row is a link, so a ctrl-click opens the case as a card does. No height written:
       with a sticky heading the table fills what `flex-1` gives it. `md` is the 36px heading.
      -->
      <mib-table
        class="min-h-0 flex-1 [&_[role=rowgroup]]:[overflow-anchor:none]"
        headerSize="md"
        [stickyHeader]="true"
        aria-label="Cases"
        [columns]="columns"
        [rows]="rows()"
        [rowKey]="rowKeyOf"
        [rowSection]="sectionOf"
        [rowLink]="{ link: 'row.href', label: 'row.title' }"
        emptyLabel="No case matches the filters."
        (rowOpened)="open($event.row.video, $event.event)"
        (rowOpenedAside)="openAside($event.row.video, $event.event)">
        <!-- The picture is decorative: the title beside it already names the case. -->
        <ng-template mibCell="title" let-row>
          <span class="flex min-w-0 items-center gap-2">
            <button
              mibCellWidget
              type="button"
              class="grid size-7 shrink-0 cursor-pointer place-items-center rounded-full hover:bg-gray-100"
              [attr.aria-pressed]="row.video.isFavourite === true"
              [attr.aria-label]="
                row.video.isFavourite ? 'Remove from favorites' : 'Add to favorites'
              "
              (click)="library.toggleFavourite(row.video)">
              <svg
                mib-icon
                name="star"
                class="size-4 [&_path]:fill-current"
                [class.text-mib-favourite]="row.video.isFavourite"
                [class.text-gray-400]="!row.video.isFavourite"></svg>
            </button>
            <span
              class="rounded-mib relative block h-9.5 w-16 shrink-0 overflow-hidden bg-gray-900">
              @if (row.thumbnail) {
                <img [ngSrc]="row.thumbnail" fill alt="" class="object-cover opacity-55" />
              }
            </span>
            <span class="text-mib-branding-900 min-w-0 truncate font-semibold">{{
              row.title
            }}</span>
          </span>
        </ng-template>

        <ng-template mibCell="categories" let-row>
          @if (row.categories.length > 0) {
            <span class="flex items-center gap-1">
              <span mibBadge mode="subtle" [palette]="paletteOf(row.categories[0])">
                {{ row.categories[0] }}
              </span>

              @if (row.categories.length > 1) {
                <button
                  mibCellWidget
                  type="button"
                  class="cursor-pointer"
                  [mibMenuTrigger]="rest"
                  [attr.aria-label]="restLabel(row.categories)">
                  <span mibBadge mode="subtle" palette="grey"
                    >+{{ row.categories.length - 1 }}</span
                  >
                </button>
                <mib-menu #rest align="end" [menuItems]="restItems(row.categories)"></mib-menu>
              }
            </span>
          }
        </ng-template>

        <ng-template mibCell="createdAt" let-row>{{
          row.createdAt | date: 'mediumDate'
        }}</ng-template>

        <ng-template mibCell="durationSeconds" let-row>
          {{ row.durationSeconds | mibDuration }}
        </ng-template>

        <ng-template mibCell="actions" let-row>
          @if (menuOf(row.video); as items) {
            <button
              mibCellWidget
              mibButton
              type="button"
              mode="ghost"
              theme="secondary"
              [mibMenuTrigger]="rowActions"
              aria-label="Case actions">
              <svg mib-icon name="ellipsis"></svg>
            </button>
            <mib-menu
              #rowActions
              align="end"
              [menuItems]="items"
              (itemSelected)="act($event, row.video)"></mib-menu>
          }
        </ng-template>

        <ng-template mibTableSectionEnd let-section>
          @if (section === favouritesToggleBand()) {
            <button
              #toggle
              mibButton
              mode="link"
              mibCellWidget
              type="button"
              [attr.aria-expanded]="library.favouritesExpanded()"
              (click)="toggleFavourites(toggle)">
              {{ favouritesToggle().label
              }}<svg
                mib-icon
                [name]="favouritesToggle().icon"
                class="transition-transform duration-mib-move ease-mib-move motion-reduce:transition-none"
                [class.rotate-180]="library.favouritesExpanded()"></svg>
            </button>
          }
        </ng-template>
      </mib-table>
    } @else if (videos().length === 0) {
      <div class="mt-2 text-center">No case matches the filters.</div>
    } @else if (placesCards()) {
      <!-- Every card is drawn in every state: a hidden one waits parked, so each move is a transition. -->
      <!-- The height moves with the cards: shrinking at once would clamp the scroll the fold keeps in step. -->
      <div
        data-cards-layout
        class="relative shrink-0"
        [style.height.px]="layout().contentH"
        [style.transition]="layout().transition">
        @let places = layout().cards;
        @let favouriteCount = markedFavourites().length;
        @if (favouriteCount > 0) {
          <section aria-labelledby="library-favourites">
            <h2 id="library-favourites" class="absolute top-0 left-0" [class]="titleTextClasses">
              Favorites
            </h2>
            @for (video of markedFavourites(); track video.id) {
              <ng-container
                [ngTemplateOutlet]="card"
                [ngTemplateOutletContext]="{
                  $implicit: video,
                  priority: $index < FIRST_ROW,
                  place: places[$index],
                  favourite: true,
                }"></ng-container>
            }
            @let tileY = layout().allFavY;
            @if (tileY !== null) {
              <!-- A transform would run ahead of the zone's layout move. -->
              <div
                class="absolute top-0 left-0 flex w-full justify-center"
                [style.width.px]="phoneWidth()"
                [style.transform]="`translateY(${tileY}px)`"
                [style.transition]="layout().transition">
                <ng-container [ngTemplateOutlet]="favouritesTile"></ng-container>
              </div>
            }
          </section>
        }
        @if (grouping() === 'strips') {
          <section aria-labelledby="library-recent">
            <h2
              id="library-recent"
              class="absolute top-0 left-0"
              [class]="titleTextClasses"
              [style.transform]="layout().recentTitle"
              [style.transition]="layout().transition">
              {{ sortTitle() }}
            </h2>
            @for (video of videos(); track video.id) {
              <ng-container
                [ngTemplateOutlet]="card"
                [ngTemplateOutletContext]="{
                  $implicit: video,
                  priority: $index < recentPriorityCount(),
                  place: places[favouriteCount + $index],
                }"></ng-container>
            }
          </section>
        }
      </div>
    } @else {
      @if (grouping() !== 'grid') {
        <div class="flex flex-col gap-4">
          @if (markedFavourites().length > 0) {
            <section class="flex flex-col gap-0.5" aria-labelledby="library-favourites">
              <h2 id="library-favourites" [class]="stripTitleClasses">Favorites</h2>
              <div #favouritesGrid [class]="gridClasses">
                @for (video of favourites(); track video.id) {
                  <ng-container
                    [ngTemplateOutlet]="card"
                    [ngTemplateOutletContext]="{
                      $implicit: video,
                      priority: $index < FIRST_ROW,
                      animated: true,
                      index: $index,
                      favourite: true,
                    }"></ng-container>
                }
                @if (showsFavouritesToggle()) {
                  <div
                    data-motion-key="tile"
                    class="place-self-center relative z-1"
                    [class.col-span-full]="favouritesToggleAlone()">
                    <ng-container [ngTemplateOutlet]="favouritesTile"></ng-container>
                  </div>
                }
              </div>
            </section>
          }
          @if (grouping() === 'strips') {
            <section #recentSection class="flex flex-col gap-0.5" aria-labelledby="library-recent">
              <h2 id="library-recent" data-motion-key="title" [class]="stripTitleClasses">
                {{ sortTitle() }}
              </h2>
              <div [class]="gridClasses">
                @for (video of videos(); track video.id) {
                  <ng-container
                    [ngTemplateOutlet]="card"
                    [ngTemplateOutletContext]="{
                      $implicit: video,
                      priority: $index < recentPriorityCount(),
                    }"></ng-container>
                }
              </div>
            </section>
          }
        </div>
      } @else {
        <div [class]="gridClasses">
          @for (video of videos(); track video.id) {
            <ng-container
              [ngTemplateOutlet]="card"
              [ngTemplateOutletContext]="{
                $implicit: video,
                priority: $index < FIRST_ROW,
              }"></ng-container>
          }
        </div>
      }
    }
  } @else if (failed()) {
    <div class="mt-2 text-center">Could not load videos</div>
  } @else if (showSpinner()) {
    <div class="flex flex-1 items-center justify-center">
      <mib-spinner size="xl"></mib-spinner>
    </div>
  }
</div>

<ng-template #favouritesTile>
  <button
    #toggle
    mibBareButton
    type="button"
    [attr.aria-expanded]="library.favouritesExpanded()"
    (click)="toggleFavourites(toggle)">
    <span
      class="group flex flex-col items-center justify-center gap-2 not-expanded:flex-row not-expanded:gap-3">
      <span
        class="border-mib-hairline flex size-18 items-center justify-center rounded-full border bg-white group-hover:bg-gray-100 group-active:bg-gray-200 not-expanded:size-10 not-expanded:border-transparent not-expanded:bg-mib-btn-primary-bg not-expanded:group-hover:bg-mib-btn-primary-bg-hover not-expanded:group-active:bg-mib-btn-primary-bg-pressed">
        <svg
          mib-icon
          [name]="favouritesToggle().icon"
          class="text-gray-700 transition-transform duration-mib-move ease-mib-move motion-reduce:transition-none not-expanded:size-4 not-expanded:text-mib-btn-primary-text"
          [class.rotate-180]="library.favouritesExpanded()"
          aria-hidden="true"></svg>
      </span>
      <span class="text-sm font-medium whitespace-nowrap text-gray-700 not-expanded:text-base">{{
        favouritesToggle().label
      }}</span>
    </span>
  </button>
</ng-template>

<ng-template
  #card
  let-video
  let-priority="priority"
  let-animated="animated"
  let-index="index"
  let-place="place"
  let-favourite="favourite">
  <!-- Deliberately unnamed: the zone's own photograph already carries the cards. -->
  <!-- A placed card's position is inline: the card's own `relative` class outranks `absolute`. -->
  <mib-case-card
    [attr.data-motion-key]="(favourite ? 'favourite:' : 'case:') + video.id"
    class="top-0 left-0 starting:[&.favourite-revealing]:translate-y-7 starting:[&.favourite-revealing]:opacity-0 motion-safe:[&.favourite-revealing]:[transition:translate_var(--transition-duration-mib-move)_var(--ease-mib-move)_var(--rise-delay),opacity_320ms_ease-out_var(--rise-delay)] starting:[&.favourite-arriving]:opacity-0 motion-safe:[&.favourite-arriving]:[transition:opacity_300ms_ease-in-out]"
    [style.position]="place ? 'absolute' : null"
    [style.will-change]="place ? 'transform, opacity' : null"
    [style.width.px]="place?.width"
    [style.transform]="place?.transform"
    [style.opacity]="place?.opacity"
    [style.transition]="place?.transition"
    [attr.inert]="place?.hidden ? '' : null"
    [style.--rise-delay]="animated ? riseDelay(index) : null"
    [animate.enter]="animated ? favouriteEnter() : ''"
    (animate.leave)="animated ? cardLeave($event, index) : $event.animationComplete()"
    [case]="caseOf(video)"
    [priority]="priority"
    addFavouriteLabel="Add to favorites"
    removeFavouriteLabel="Remove from favorites"
    (opened)="open(video, $event)"
    (favouriteToggled)="library.toggleFavourite(video)"
    (shared)="onShare(video)"
    (openedAside)="openAside(video, $event)">
    <!-- The card lays whatever it is given under the title, one line each. -->
    <p class="flex items-center justify-between gap-2">
      {{ video.createdAt | date: 'mediumDate' }}
      @if (video.stats.videoCutCount > 0) {
        <mai-case-stats [videoStats]="video.stats"></mai-case-stats>
      }
    </p>

    <!-- The menu is ours, and so are the rights behind it. -->
    @if (menuOf(video); as items) {
      <mib-menu align="end" [menuItems]="items" (itemSelected)="act($event, video)"></mib-menu>
    }
  </mib-case-card>
</ng-template>
